// === Admin: Discount Codes ===
// Full CRUD page — list, add, edit, soft-delete, send email.
// Ported from Click2Gro PHP (DiscountCodesController.php / discount_codes table)
// Fields: code, name, email_address, notes, percentage_discount, duration_in_months,
//         stripe_coupon_id (read-only), status, email_sent_date (read-only)

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PERCENTAGE_OPTIONS = [10, 15, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 100];
const DURATION_OPTIONS   = [1, 2, 3, 4, 5, 6, 9, 12];

function getApiUrl(): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8787';
  }
  return 'https://api.click2gro.com.au';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// Generate a random 6-character alphanumeric code
function generateCode(): string {
  return Math.random().toString(36).toUpperCase().slice(2, 8);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface DiscountCodeRow {
  readonly id: number;
  readonly code: string;
  readonly name: string | null;
  readonly email_address: string | null;
  readonly percentage_discount: string | null;
  readonly duration_in_months: number | null;
  readonly stripe_coupon_id: string | null;
  readonly status: number;
  readonly email_sent_date: string | null;
  readonly notes: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

interface FormState {
  code: string;
  name: string;
  email_address: string;
  notes: string;
  percentage_discount: number | '';
  duration_in_months: number | '';
  status: number;
}

const DEFAULT_FORM: FormState = {
  code:                '',
  name:                '',
  email_address:       '',
  notes:               '',
  percentage_discount: '',
  duration_in_months:  '',
  status:              1,
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export function DiscountCodesPage() {
  const navigate  = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // List state
  const [rows,    setRows]    = useState<DiscountCodeRow[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(false);
  const LIMIT = 15;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId,  setEditingId]  = useState<number | null>(null);
  const [form,       setForm]       = useState<FormState>(DEFAULT_FORM);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [saving,     setSaving]     = useState(false);

  // Read-only fields from DB (shown on edit)
  const [stripeCouponId,  setStripeCouponId]  = useState<string | null>(null);
  const [emailSentDate,   setEmailSentDate]   = useState<string | null>(null);

  // Delete dialog
  const [deleteId,    setDeleteId]    = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting,    setDeleting]    = useState(false);

  // Send email
  const [sendingEmailId, setSendingEmailId] = useState<number | null>(null);

  // Snackbar
  const [snackMsg,  setSnackMsg]  = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const showSnack = (msg: string) => { setSnackMsg(msg); setSnackOpen(true); };

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/discount-codes`);
      url.searchParams.set('page',   String(page));
      url.searchParams.set('limit',  String(LIMIT));
      url.searchParams.set('search', search);
      const res  = await fetch(url.toString(), { headers: authHeaders(token) });
      const json = (await res.json()) as { data: DiscountCodeRow[]; total: number };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch {
      // network error — ignore
    } finally {
      setLoading(false);
    }
  }, [token, page, search]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add dialog
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setStripeCouponId(null);
    setEmailSentDate(null);
    setFormError(null);
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Open Edit dialog — fetch latest record
  // ---------------------------------------------------------------------------
  const openEdit = async (row: DiscountCodeRow) => {
    if (!token) return;
    setFormError(null);
    try {
      const res  = await fetch(`${getApiUrl()}/api/v1/admin/discount-codes/${row.id}`, { headers: authHeaders(token) });
      const json = (await res.json()) as { data: DiscountCodeRow };
      const r    = json.data;
      setEditingId(r.id);
      setForm({
        code:                r.code ?? '',
        name:                r.name ?? '',
        email_address:       r.email_address ?? '',
        notes:               r.notes ?? '',
        percentage_discount: r.percentage_discount ? Number(r.percentage_discount) : '',
        duration_in_months:  r.duration_in_months ?? '',
        status:              r.status ?? 1,
      });
      setStripeCouponId(r.stripe_coupon_id);
      setEmailSentDate(r.email_sent_date);
    } catch {
      setForm({ ...DEFAULT_FORM, ...{ code: row.code, name: row.name ?? '', email_address: row.email_address ?? '' } });
      setEditingId(row.id);
    }
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Generate unique code
  // ---------------------------------------------------------------------------
  const handleGenerateCode = async () => {
    if (!token) return;
    let candidate = generateCode();
    // Check up to 5 times for uniqueness
    for (let i = 0; i < 5; i++) {
      const res  = await fetch(`${getApiUrl()}/api/v1/admin/discount-codes/check-code`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ code: candidate, excludeId: editingId }),
      });
      const json = (await res.json()) as { exists: boolean };
      if (!json.exists) break;
      candidate = generateCode();
    }
    setForm((f) => ({ ...f, code: candidate }));
  };

  // ---------------------------------------------------------------------------
  // Save (create or update)
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const code = form.code.trim().toUpperCase();
    if (!code) { setFormError('Discount code is required.'); return; }
    if (!form.email_address.trim()) { setFormError('Email address is required.'); return; }
    if (!form.percentage_discount) { setFormError('Percentage discount is required.'); return; }
    if (!form.duration_in_months) { setFormError('Duration is required.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url    = editingId
        ? `${getApiUrl()}/api/v1/admin/discount-codes/${editingId}`
        : `${getApiUrl()}/api/v1/admin/discount-codes`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          code,
          name:                form.name.trim() || null,
          email_address:       form.email_address.trim(),
          notes:               form.notes.trim() || null,
          percentage_discount: form.percentage_discount,
          duration_in_months:  form.duration_in_months,
          status:              form.status,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Record updated.' : 'Record created.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/discount-codes/${deleteId}`, {
        method: 'DELETE', headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch {
      showSnack('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Send Email
  // ---------------------------------------------------------------------------
  const handleSendEmail = async (id: number, email: string | null) => {
    if (!token) return;
    if (!email) { showSnack('No email address on this record.'); return; }
    setSendingEmailId(id);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/discount-codes/${id}/send-email`, {
        method: 'POST', headers: authHeaders(token),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      showSnack(`Email sent to ${email}`);
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to send email.');
    } finally {
      setSendingEmailId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Discount Codes</Typography>
          <Typography variant="body2" color="text.secondary">Manage promo discount codes</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Code
        </Button>
      </Box>

      {/* Search bar */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search codes, names, emails..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {total} record{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <AppTableHead>
              <TableRow>
                <AppTableHeadCell sx={{ minWidth: 130 }}>Code</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 160 }}>Name</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 180 }}>Email</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 90 }}>Discount</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 90 }}>Duration</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 90 }}>Status</AppTableHeadCell>
                <AppTableHeadCell sx={{ minWidth: 130, whiteSpace: 'nowrap' }}>Email Sent</AppTableHeadCell>
                <AppTableHeadCell align="right" sx={{ minWidth: 120 }}>Actions</AppTableHeadCell>
              </TableRow>
            </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PriceIcon fontSize="small" sx={{ color: '#f57f17', flexShrink: 0 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: 1 }}>
                        {row.code}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                    {row.name ?? '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                    {row.email_address ?? '—'}
                  </TableCell>
                  <TableCell>
                    {row.percentage_discount ? (
                      <Chip label={`${row.percentage_discount}%`} size="small" color="success" />
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                    {row.duration_in_months ? `${row.duration_in_months} mo` : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status === 1 ? 'Active' : 'Inactive'}
                      size="small"
                      color={row.status === 1 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                    {fmtDate(row.email_sent_date)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title={`Send email to ${row.email_address ?? 'N/A'}`}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={!row.email_address || sendingEmailId === row.id}
                            onClick={() => void handleSendEmail(row.id, row.email_address)}
                          >
                            {sendingEmailId === row.id
                              ? <CircularProgress size={16} />
                              : <SendIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => void openEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, row.code)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingId ? 'Edit Discount Code' : 'Add Discount Code'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>

            {/* Code + Generate */}
            <Grid item xs={12}>
              <TextField
                label="Discount Code *"
                fullWidth
                size="small"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 120 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate unique code">
                        <IconButton size="small" onClick={() => void handleGenerateCode()}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                label="Email Address *"
                fullWidth
                size="small"
                type="email"
                value={form.email_address}
                onChange={(e) => setForm((f) => ({ ...f, email_address: e.target.value }))}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            {/* Percentage Discount */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Percentage Discount *</InputLabel>
                <Select
                  label="Percentage Discount *"
                  value={form.percentage_discount}
                  onChange={(e) => setForm((f) => ({ ...f, percentage_discount: Number(e.target.value) as number }))}
                >
                  {PERCENTAGE_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p === 100 ? '100% (cardless signup)' : `${p}%`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Duration */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Duration (Months) *</InputLabel>
                <Select
                  label="Duration (Months) *"
                  value={form.duration_in_months}
                  onChange={(e) => setForm((f) => ({ ...f, duration_in_months: Number(e.target.value) as number }))}
                >
                  {DURATION_OPTIONS.map((m) => (
                    <MenuItem key={m} value={m}>{m} month{m !== 1 ? 's' : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status === 1}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label={form.status === 1 ? 'Active' : 'Inactive'}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                size="small"
                multiline
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>

            {/* Read-only fields shown on edit */}
            {editingId && stripeCouponId && (
              <Grid item xs={12}>
                <TextField
                  label="Stripe Coupon ID"
                  fullWidth
                  size="small"
                  value={stripeCouponId}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                />
              </Grid>
            )}
            {editingId && emailSentDate && (
              <Grid item xs={12}>
                <TextField
                  label="Email Sent"
                  fullWidth
                  size="small"
                  value={fmtDateTime(emailSentDate)}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                />
              </Grid>
            )}

          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {editingId ? 'Save Changes' : 'Add Code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirm Dialog                                                */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Discount Code?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void handleDelete()} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3500}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}
